import { useState, useEffect, useRef } from "react";
import useFlow from "../hooks/useFlow";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal } from "react-bootstrap";
import useStore from "../hooks/useStore";
import { clearHubSpotState } from "../utils/hubspot";
import TextInputWithKeyboard from "./TextInputWithKeyboard";
import PhoneInputWithNumpad from "./PhoneInputWithNumpad";

export default function RegisterModal({ mode }) {
  const {
    selectedSiteId,
    createAccount,
    createAccountMbo,
    showRegisterModal,
    closeRegisterModal,
    openTermsModal,
    acceptTerms,
    resetTerms,
    createAccountOld,
    locationPreferences,
    setRegisteredClientPhone,
    registerJoinPending,
  } = useFlow();
  const { setShowPlansOfferModal, setSelectedClient } = useStore();

  const [loading, setLoading] = useState(false);

  const firstNameInputRef = useRef(null);

  useEffect(() => {
    if (showRegisterModal) firstNameInputRef.current.focus();
    else formik.resetForm();
  }, [showRegisterModal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (acceptTerms > 0) formik.setFieldValue("terms", true);
  }, [acceptTerms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Define validation schema with Yup
  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required."),
    lastName: Yup.string().required("Last name is required."),
    email: Yup.string()
      .email("Email is not valid.")
      .required("Email is required."),
    phoneNumber: Yup.string()
      .matches(/\(\d{3}\) \d{3}-\d{4}/, "Phone number is not valid.")
      .required("Phone number is required."),
    password:
      mode === "old" || mode === "kc"
        ? Yup.string()
            .required("Password is required.")
            .matches(
              /^(?=.*[A-Za-z])(?=.*\d)/,
              "Password must contain at least one letter and one number."
            )
            .min(8, "Password must be at least 8 characters long.")
            .max(32, "Password must be at most 32 characters long.")
        : Yup.string(),
    terms: Yup.boolean().oneOf(
      [true],
      "You must accept the terms and conditions."
    ),
    birthDate:
      selectedSiteId === "224258"
        ? Yup.string().required("Date of birth is required.")
        : Yup.string(),
  });

  // Initialize Formik
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      terms: true,
      birthDate: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      // Clean the phone number before sending it to the submit
      const cleanValues = {
        ...values,
        phoneNumber: values.phoneNumber.replace(/\D/g, ""),
      };

      try {
        const resp =
          mode === "kc"
            ? await createAccount(cleanValues)
            : mode === "mbo"
            ? await createAccountMbo(cleanValues)
            : await createAccountOld(cleanValues);
        if (resp) {
          toast.success(`Account created successfully!`);
          resetTerms();
          formik.resetForm();
          closeRegisterModal();
          setSelectedClient(resp);
          setRegisteredClientPhone(cleanValues.phoneNumber);

          /**
           * On a Free Day, someone who signed up to get into a class goes
           * straight in. The plans modal here is not driven by a payment
           * failure — it opens on every account creation — and on a free day it
           * asks a client to pay for something the API is about to cover, which
           * is the one thing this modal should never do.
           *
           * Only for the class-join entry point. From the home screen or the
           * store there is no class to get into, so the offer still shows.
           *
           * Nothing is lost if the free pass does not work out: `join()` opens
           * this same modal when the API answers ClassRequiresPayment, so the
           * payment path is intact and merely stops being the first thing the
           * client sees.
           */
          const freeDayJoin =
            registerJoinPending && locationPreferences?.freeDay?.active === true;
          setShowPlansOfferModal(!freeDayJoin);
          console.log("clearing hubspot", window?.HubSpotConversations);
          clearHubSpotState();
        }
      } catch (e) {
        toast.error(`Something went wrong! ${e?.message}`);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleTermsLinkClick = (e) => {
    e.preventDefault();
    if (loading) return;
    closeRegisterModal();
    openTermsModal();
  };

  return (
    <Modal
      show={showRegisterModal}
      onHide={closeRegisterModal}
      centered
      size="lg"
    >
      <Modal.Body className="p-5">
        <button
          type="button"
          className="btn-close"
          data-testid="register-modal-close-btn"
          onClick={closeRegisterModal}
        ></button>
        <h3 className="text-center">Create your account</h3>
        <p className="text-center">
          You&apos;ll use this to login to the website and sign up for classes
        </p>
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          <div className="row justify-content-center py-5">
            <div className="col-12 col-md-8 py-2">
              <TextInputWithKeyboard
                type="text"
                inputClassName={
                  formik.errors.firstName && formik.touched.firstName
                    ? "is-invalid"
                    : ""
                }
                placeholder="First name"
                name="firstName"
                value={formik.values.firstName}
                onChange={(val) => formik.setFieldValue("firstName", val)}
                onBlur={() => formik.setFieldTouched("firstName", true)}
                disabled={loading}
                inputRef={firstNameInputRef}
                showKeyboard={locationPreferences?.showFullKeyboard}
              />
              {formik.errors.firstName && formik.touched.firstName && (
                <div className="invalid-feedback">
                  {formik.errors.firstName}
                </div>
              )}
            </div>

            <div className="col-12 col-md-8 py-2">
              <TextInputWithKeyboard
                type="text"
                inputClassName={
                  formik.errors.lastName && formik.touched.lastName
                    ? "is-invalid"
                    : ""
                }
                placeholder="Last name"
                name="lastName"
                value={formik.values.lastName}
                onChange={(val) => formik.setFieldValue("lastName", val)}
                onBlur={() => formik.setFieldTouched("lastName", true)}
                disabled={loading}
                showKeyboard={locationPreferences?.showFullKeyboard}
              />
              {formik.errors.lastName && formik.touched.lastName && (
                <div className="invalid-feedback">{formik.errors.lastName}</div>
              )}
            </div>

            <div className="col-12 col-md-8 py-2">
              <TextInputWithKeyboard
                type="email"
                inputClassName={
                  formik.errors.email && formik.touched.email
                    ? "is-invalid"
                    : ""
                }
                placeholder="Email"
                name="email"
                value={formik.values.email}
                onChange={(val) => formik.setFieldValue("email", val)}
                onBlur={() => formik.setFieldTouched("email", true)}
                disabled={loading}
                showKeyboard={locationPreferences?.showFullKeyboard}
              />
              {formik.errors.email && formik.touched.email && (
                <div className="invalid-feedback">{formik.errors.email}</div>
              )}
            </div>

            <div className="col-12 col-md-8 py-2">
              <PhoneInputWithNumpad
                value={formik.values.phoneNumber}
                onChange={(val) => formik.setFieldValue("phoneNumber", val)}
                onBlur={() => formik.setFieldTouched("phoneNumber", true)}
                showNumpad={locationPreferences?.showPhoneNumpad}
                disabled={loading}
                inputClassName={
                  formik.errors.phoneNumber && formik.touched.phoneNumber
                    ? "is-invalid"
                    : ""
                }
              />
              {formik.errors.phoneNumber && formik.touched.phoneNumber && (
                <div className="invalid-feedback">
                  {formik.errors.phoneNumber}
                </div>
              )}
            </div>

            {(mode === "old" || mode === "kc") && (
              <div className="col-12 col-md-8 py-2">
                <TextInputWithKeyboard
                  type="password"
                  inputClassName={
                    formik.errors.password && formik.touched.password
                      ? "is-invalid"
                      : ""
                  }
                  placeholder="Password"
                  name="password"
                  value={formik.values.password}
                  onChange={(val) => formik.setFieldValue("password", val)}
                  onBlur={() => formik.setFieldTouched("password", true)}
                  disabled={loading}
                  showKeyboard={locationPreferences?.showFullKeyboard}
                />
                {formik.errors.password && formik.touched.password && (
                  <div className="invalid-feedback">
                    {formik.errors.password}
                  </div>
                )}
              </div>
            )}

            {selectedSiteId === "224258" && (
              <div className="col-12 col-md-8 py-2">
<input
                type="text"
                onFocus={(e) => (e.target.type = "date")}
                onBlur={formik.handleBlur}
                className="form-control form-control-lg"
                placeholder="Date of birth"
                name="birthDate"
                value={formik.values.birthDate}
                onChange={formik.handleChange}
                disabled={loading}
                autoComplete="off"
              />
              </div>
            )}

            <div className="col-12 col-md-8 py-2 form-check text-center">
              <input
                type="checkbox"
                className={`me-1 ${
                  formik.errors.terms && formik.touched.terms
                    ? "is-invalid"
                    : ""
                }`}
                data-testid="register-terms-checkbox"
                name="terms"
                checked={formik.values.terms}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="terms">
                I agree to the{" "}
                <a href="#" data-testid="register-terms-link" onClick={handleTermsLinkClick}>
                  Terms
                </a>
              </label>
              {formik.errors.terms && formik.touched.terms && (
                <div className="invalid-feedback">{formik.errors.terms}</div>
              )}
            </div>

            <div className="col-sm-12 pt-5 text-center">
              <input
                type="submit"
                className="btn btn-primary btn-lg"
                data-testid="register-submit-btn"
                value={loading ? "Creating account..." : "Create account"}
                disabled={
                  loading ||
                  Object.keys(formik.errors).length > 0 ||
                  Object.keys(formik.touched).length === 0
                }
              />
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
